// frontend/src/settings/AuditLogSettings.js
// 組織 admin 向け 監査ログ閲覧ページ
// - 自組織 (currentOrganization.id) の audit_logs のみ表示 (RLS で自動フィルタ)
// - 期間 / actor / action / target フィルタ + CSV エクスポート

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import { supabase } from '../supabase';
import { useOrganization } from '../contexts/OrganizationContext';

const ACTION_LABELS = {
  login: 'ログイン', login_failed: 'ログイン失敗', logout: 'ログアウト',
  create: '作成', update: '更新', delete: '削除', read: '閲覧',
  access_denied: 'アクセス拒否',
  edit_session_start: '編集ON', edit_session_end: '編集OFF',
  other: 'その他',
};
const ACTOR_LABELS = {
  organization_staff: '施設スタッフ', organization_admin: '施設管理者',
  internal_staff: '自社スタッフ', operator: '運営', anonymous: '未認証',
};

const PERIOD_OPTIONS = [
  { value: '1d', label: '過去1日', days: 1 },
  { value: '7d', label: '過去7日', days: 7 },
  { value: '30d', label: '過去30日', days: 30 },
  { value: '90d', label: '過去90日', days: 90 },
  { value: '1y', label: '過去1年', days: 365 },
  { value: 'all', label: '全期間', days: null },
];

const AuditLogSettings = () => {
  const { currentOrganization } = useOrganization();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('7d');
  const [filterAction, setFilterAction] = useState('all');
  const [filterActor, setFilterActor] = useState('all');
  const [search, setSearch] = useState('');

  const fetchLogs = useCallback(async () => {
    if (!currentOrganization?.id) return;
    setLoading(true); setError('');
    try {
      const opt = PERIOD_OPTIONS.find((p) => p.value === period) || PERIOD_OPTIONS[2];
      const since = opt.days ? dayjs().subtract(opt.days, 'day').toISOString() : '2000-01-01T00:00:00Z';
      let q = supabase
        .from('audit_logs')
        .select('id, organization_id, actor_user_id, actor_email, actor_type, actor_role, action_type, target_table, target_id, target_summary, changes, ip_address, user_agent, success, error_message, created_at')
        .eq('organization_id', currentOrganization.id)
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(2000);
      const { data, error: e } = await q;
      if (e) throw e;
      setLogs(data || []);
    } catch (e) {
      setError(`取得エラー: ${e.message}`);
    } finally { setLoading(false); }
  }, [currentOrganization?.id, period]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const filtered = useMemo(() => {
    const kw = search.trim().toLowerCase();
    return logs.filter((l) => {
      if (filterAction !== 'all' && l.action_type !== filterAction) return false;
      if (filterActor !== 'all' && l.actor_type !== filterActor) return false;
      if (kw) {
        const hay = [l.actor_email, l.target_table, l.target_id, l.target_summary]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(kw)) return false;
      }
      return true;
    });
  }, [logs, filterAction, filterActor, search]);

  const exportCsv = () => {
    const headers = ['created_at', 'actor_email', 'actor_type', 'actor_role', 'action_type', 'target_table', 'target_id', 'target_summary', 'ip_address', 'user_agent', 'success', 'error_message'];
    const escape = (v) => {
      if (v == null) return '';
      const s = String(v);
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const rows = filtered.map((l) => headers.map((h) => escape(l[h])).join(','));
    const csv = '﻿' + headers.join(',') + '\n' + rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `audit_logs_${dayjs().format('YYYYMMDD_HHmm')}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  return (
    <div style={{ padding: 20, maxWidth: 1200, margin: '0 auto' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, marginBottom: 6 }}>🔍 監査ログ</h1>
      <p style={{ color: '#64748b', fontSize: 13, marginTop: 0 }}>
        自組織で発生した認証・データ変更などの操作履歴です。 改ざん防止のため追記のみ、 削除不可。
      </p>

      {error && (
        <div style={{ background: '#fee2e2', color: '#7f1d1d', padding: 10, borderRadius: 8, fontSize: 12, marginBottom: 12 }}>{error}</div>
      )}

      {/* フィルタ */}
      <div style={{
        background: 'white', padding: 12, borderRadius: 10, border: '1px solid #e5e7eb',
        display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12,
      }}>
        <label style={{ fontSize: 12 }}>
          期間:{' '}
          <select value={period} onChange={(e) => setPeriod(e.target.value)} style={sel}>
            {PERIOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 12 }}>
          操作:{' '}
          <select value={filterAction} onChange={(e) => setFilterAction(e.target.value)} style={sel}>
            <option value="all">全て</option>
            {Object.entries(ACTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 12 }}>
          実行者:{' '}
          <select value={filterActor} onChange={(e) => setFilterActor(e.target.value)} style={sel}>
            <option value="all">全て</option>
            {Object.entries(ACTOR_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="🔍 メアド / テーブル / 要約で検索"
          style={{ ...sel, minWidth: 240 }} />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button onClick={fetchLogs} style={btnSecondary}>↻ 再読み込み</button>
          <button onClick={exportCsv} disabled={filtered.length === 0} style={{
            ...btnPrimary,
            opacity: filtered.length === 0 ? 0.4 : 1,
            cursor: filtered.length === 0 ? 'not-allowed' : 'pointer',
          }}>
            📥 CSV エクスポート ({filtered.length})
          </button>
        </div>
      </div>

      {/* 一覧 */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>読み込み中...</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>該当ログがありません</div>
      ) : (
        <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e5e7eb', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
              <tr>
                <th style={th}>日時</th>
                <th style={th}>実行者</th>
                <th style={th}>種別</th>
                <th style={th}>操作</th>
                <th style={th}>対象</th>
                <th style={{ ...th, textAlign: 'left' }}>要約</th>
                <th style={th}>IP</th>
                <th style={th}>結果</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id} style={{ borderBottom: '1px solid #f1f5f9', background: l.success === false ? '#fef2f2' : 'transparent' }}>
                  <td style={td}>{dayjs(l.created_at).format('MM/DD HH:mm:ss')}</td>
                  <td style={{ ...td, textAlign: 'left', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {l.actor_email || '(anon)'}
                    {l.actor_role && <span style={{ color: '#94a3b8', marginLeft: 4 }}>[{l.actor_role}]</span>}
                  </td>
                  <td style={td}>
                    <span style={{
                      padding: '2px 6px', borderRadius: 8, fontSize: 10,
                      background: actorBg(l.actor_type), color: 'white', fontWeight: 700,
                    }}>{ACTOR_LABELS[l.actor_type] || l.actor_type}</span>
                  </td>
                  <td style={td}>
                    <span style={{
                      padding: '2px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700,
                      background: actionBg(l.action_type), color: actionFg(l.action_type),
                    }}>{ACTION_LABELS[l.action_type] || l.action_type}</span>
                  </td>
                  <td style={{ ...td, fontSize: 11, color: '#475569' }}>
                    {l.target_table || '-'}
                    {l.target_id && <div style={{ fontSize: 9, color: '#94a3b8' }}>{l.target_id}</div>}
                  </td>
                  <td style={{ ...td, textAlign: 'left', fontSize: 11 }}>
                    {l.target_summary || <span style={{ color: '#94a3b8' }}>-</span>}
                    {l.error_message && <div style={{ fontSize: 10, color: '#dc2626', marginTop: 2 }}>{l.error_message}</div>}
                  </td>
                  <td style={{ ...td, fontFamily: 'Menlo, monospace', fontSize: 10, color: '#64748b' }}>
                    {l.ip_address || '-'}
                  </td>
                  <td style={td}>
                    {l.success === false
                      ? <span style={{ color: '#dc2626', fontWeight: 700 }}>✕</span>
                      : <span style={{ color: '#16a34a', fontWeight: 700 }}>✓</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 8 }}>
        監査ログは改ざん防止のため追記のみ許可。 削除・編集は禁止。 保存期間: 5年以上を想定。
      </div>
    </div>
  );
};

const sel = { padding: '4px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12 };
const btnPrimary = { padding: '6px 14px', background: 'linear-gradient(135deg, #1d4ed8, #1e3a8a)', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' };
const btnSecondary = { padding: '6px 14px', background: '#e5e7eb', color: '#334155', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' };
const th = { padding: 8, textAlign: 'center', fontWeight: 700, color: '#475569', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e5e7eb' };
const td = { padding: '6px 8px', textAlign: 'center' };

const actorBg = (t) => ({
  organization_staff: '#3b82f6', organization_admin: '#7c3aed',
  internal_staff: '#0891b2', operator: '#dc2626', anonymous: '#94a3b8',
})[t] || '#94a3b8';
const actionBg = (t) => ({
  login: '#dbeafe', logout: '#f1f5f9', login_failed: '#fee2e2', access_denied: '#fee2e2',
  create: '#dcfce7', update: '#fef9c3', delete: '#fee2e2', read: '#e0e7ff',
  edit_session_start: '#fef3c7', edit_session_end: '#fef3c7',
})[t] || '#f1f5f9';
const actionFg = (t) => ({
  login: '#1d4ed8', logout: '#475569', login_failed: '#991b1b', access_denied: '#991b1b',
  create: '#166534', update: '#854d0e', delete: '#991b1b', read: '#3730a3',
  edit_session_start: '#78350f', edit_session_end: '#78350f',
})[t] || '#475569';

export default AuditLogSettings;
